import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { ENTITY_LABEL } from '@cms/shared/entity-labels'
import { useAuthStore } from '@/stores/auth'
import { usePlatformAuthStore } from '@/stores/platform-auth'
import { me as apiMe } from '@/api/auth'
import { platformMe as apiPlatformMe } from '@/api/platform'
import { getSessionToken } from '@/utils/auth-session'
import { getPageSessionScope } from '@/utils/session-scope'
import { loadRoute } from './lazy-route'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => loadRoute(() => import('@/views/auth/Login.vue')),
    meta: { public: true, title: '登录' },
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => loadRoute(() => import('@/views/auth/ChangePassword.vue')),
    meta: { requiresAuth: true, title: '修改密码' },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => loadRoute(() => import('@/views/auth/Forbidden.vue')),
    meta: { public: true, title: '无访问权限' },
  },
  {
    path: '/subscription-expired',
    name: 'subscription-expired',
    component: () => loadRoute(() => import('@/views/SubscriptionExpired.vue')),
    meta: { requiresAuth: true, title: '服务已到期' },
  },
  // ===== 平台超管入口（add-platform-admin-web）：与员工工作台会话独立 =====
  {
    path: '/admin/login',
    name: 'platform-login',
    component: () => loadRoute(() => import('@/views/platform/AdminLogin.vue')),
    meta: { public: true, platform: true, title: '平台管理登录' },
  },
  {
    path: '/admin',
    component: () => loadRoute(() => import('@/layouts/AdminPlatformLayout.vue')),
    meta: { platform: true },
    children: [
      {
        path: 'change-password',
        name: 'platform-change-password',
        component: () => loadRoute(() => import('@/views/platform/ChangePassword.vue')),
        meta: { platform: true, title: '修改密码' },
      },
      {
        path: 'forbidden',
        name: 'platform-forbidden',
        component: () => loadRoute(() => import('@/views/auth/Forbidden.vue')),
        meta: { platform: true, title: '无访问权限' },
      },
      {
        path: '',
        name: 'platform-customer-dashboard',
        component: () => loadRoute(() => import('@/views/platform/CustomerDashboard.vue')),
        meta: {
          platform: true,
          title: '业务总览',
          platformPermission: 'platform.customers.read',
        },
      },
      {
        path: 'overview',
        name: 'platform-overview',
        component: () => loadRoute(() => import('@/views/platform/Overview.vue')),
        meta: { platform: true, title: '平台总览', platformPermission: 'platform.overview.read' },
      },
      {
        path: 'tenants',
        name: 'platform-tenants',
        redirect: { path: '/admin/customers', query: { scope: 'all' } },
        meta: { platform: true, title: '企业客户', platformPermission: 'platform.customers.read' },
      },
      {
        path: 'tenants/:id',
        name: 'platform-tenant-detail',
        component: () => loadRoute(() => import('@/views/platform/TenantDetail.vue')),
        meta: { platform: true, title: '企业空间详情', platformPermission: 'platform.tenants.read' },
      },
      {
        path: 'orders',
        name: 'platform-orders',
        component: () => loadRoute(() => import('@/views/platform/SalesOrders.vue')),
        meta: { platform: true, title: '销售订单', platformPermission: 'platform.order.read' },
      },
      {
        path: 'orders/new',
        name: 'platform-order-create',
        component: () => loadRoute(() => import('@/views/platform/SalesOrderCreate.vue')),
        meta: {
          platform: true,
          title: '新建销售订单',
          platformPermission: 'platform.order.create',
        },
      },
      {
        path: 'orders/:id',
        name: 'platform-order-detail',
        component: () => loadRoute(() => import('@/views/platform/SalesOrderDetail.vue')),
        meta: { platform: true, title: '销售订单详情', platformPermission: 'platform.order.read' },
      },
      {
        path: 'lifecycle',
        name: 'platform-lifecycle',
        component: () => loadRoute(() => import('@/views/platform/LifecycleDashboard.vue')),
        meta: {
          platform: true,
          title: '续费运营',
          platformPermission: 'platform.lifecycle.read',
        },
      },
      {
        path: 'products',
        name: 'platform-products',
        component: () => loadRoute(() => import('@/views/platform/Products.vue')),
        meta: { platform: true, title: '平台服务产品', platformPermission: 'platform.catalog.read' },
      },
      {
        path: 'products/:id',
        name: 'platform-product-detail',
        component: () => loadRoute(() => import('@/views/platform/ProductDetail.vue')),
        meta: { platform: true, title: '平台服务产品详情', platformPermission: 'platform.catalog.read' },
      },
      {
        path: 'packages',
        name: 'platform-packages',
        component: () => loadRoute(() => import('@/views/platform/Packages.vue')),
        meta: { platform: true, title: '订阅套餐', platformPermission: 'platform.catalog.read' },
      },
      {
        path: 'packages/:id',
        name: 'platform-package-detail',
        component: () => loadRoute(() => import('@/views/platform/PackageDetail.vue')),
        meta: { platform: true, title: '订阅套餐详情', platformPermission: 'platform.catalog.read' },
      },
      {
        path: 'customers',
        name: 'platform-customers',
        component: () => loadRoute(() => import('@/views/platform/Customers.vue')),
        meta: { platform: true, title: '商业客户', platformPermission: 'platform.customers.read' },
      },
      {
        path: 'customers/:id',
        name: 'platform-customer-detail',
        component: () => loadRoute(() => import('@/views/platform/CustomerDetail.vue')),
        meta: { platform: true, title: '客户详情', platformPermission: 'platform.customers.read' },
      },
      {
        path: 'people',
        name: 'platform-people',
        component: () => loadRoute(() => import('@/views/platform/People.vue')),
        meta: { platform: true, title: '平台人员', platformPermission: 'platform.people.read' },
      },
      {
        path: 'teams',
        name: 'platform-teams',
        component: () => loadRoute(() => import('@/views/platform/Teams.vue')),
        meta: { platform: true, title: '销售团队', platformPermission: 'platform.teams.read' },
      },
      {
        path: 'roles',
        name: 'platform-roles',
        component: () => loadRoute(() => import('@/views/platform/Roles.vue')),
        meta: { platform: true, title: '平台角色', platformPermission: 'platform.roles.read' },
      },
      {
        path: 'audit',
        name: 'platform-audit',
        component: () => loadRoute(() => import('@/views/platform/AuditLogs.vue')),
        meta: { platform: true, title: '操作审计', platformPermission: 'platform.audit.read' },
      },
      {
        path: 'security',
        name: 'platform-security',
        component: () => loadRoute(() => import('@/views/platform/SecurityCenter.vue')),
        meta: { platform: true, title: '安全中心', platformPermission: 'platform.security.read' },
      },
    ],
  },
  {
    path: '/',
    component: () => loadRoute(() => import('@/layouts/AdminLayout.vue')),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => loadRoute(() => import('@/views/Dashboard.vue')),
      },
      ...(import.meta.env.DEV || import.meta.env.MODE === 'test'
        ? [
            {
              path: 'design-system',
              name: 'design-system',
              component: () => loadRoute(() => import('@/views/DesignSystem.vue')),
              meta: { title: 'Design System', group: 'Internal' },
            },
          ]
        : []),
      {
        path: 'leads',
        name: 'lead-list',
        component: () => loadRoute(() => import('@/views/lead/Leads.vue')),
        meta: { title: '线索管理', group: '业务管理' },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => loadRoute(() => import('@/views/Notifications.vue')),
        meta: { title: '内部通知', group: '工作区' },
      },
      {
        path: 'leads/:id',
        name: 'lead-detail',
        component: () => loadRoute(() => import('@/views/lead/LeadDetail.vue')),
        meta: { title: '线索详情', group: '业务管理' },
      },
      {
        path: 'projects',
        name: 'project-list',
        component: () => loadRoute(() => import('@/views/project/Projects.vue')),
        meta: { title: '项目管理', group: '业务管理' },
      },
      {
        path: 'projects/create',
        name: 'project-create',
        component: () => loadRoute(() => import('@/views/project/ProjectCreate.vue')),
        meta: { title: '新建项目', group: '业务管理' },
      },
      {
        path: 'projects/:id/edit',
        name: 'project-edit',
        component: () => loadRoute(() => import('@/views/project/ProjectCreate.vue')),
        meta: { title: '编辑项目', group: '业务管理' },
      },
      {
        path: 'projects/:id',
        name: 'project-detail',
        component: () => loadRoute(() => import('@/views/project/ProjectDetail.vue')),
        meta: { title: '项目详情', group: '业务管理' },
      },
      // 库存：五个模块各自专属页面（inventory-module-pages）
      {
        path: 'inventory/balances',
        name: 'inventory-balances',
        component: () => loadRoute(() => import('@/views/inventory/Balances.vue')),
        meta: { title: '库存余额', group: '业务管理' },
      },
      {
        path: 'inventory/ledgers',
        name: 'inventory-ledgers',
        component: () => loadRoute(() => import('@/views/inventory/Ledgers.vue')),
        meta: { title: '库存流水', group: '业务管理' },
      },
      {
        path: 'inventory/inbounds',
        name: 'inventory-inbounds',
        component: () => loadRoute(() => import('@/views/inventory/Documents.vue')),
        meta: { title: '入库单', group: '业务管理' },
      },
      {
        path: 'inventory/inbounds/:id',
        name: 'inventory-inbound-detail',
        component: () => loadRoute(() => import('@/views/inventory/DocumentDetail.vue')),
        meta: { title: '入库单详情', group: '业务管理' },
      },
      {
        path: 'inventory/issues',
        name: 'inventory-issues',
        component: () => loadRoute(() => import('@/views/inventory/Documents.vue')),
        meta: { title: '领料单', group: '业务管理' },
      },
      {
        path: 'inventory/issues/:id',
        name: 'inventory-issue-detail',
        component: () => loadRoute(() => import('@/views/inventory/DocumentDetail.vue')),
        meta: { title: '领料单详情', group: '业务管理' },
      },
      {
        path: 'inventory/returns',
        name: 'inventory-returns',
        component: () => loadRoute(() => import('@/views/inventory/Documents.vue')),
        meta: { title: '退料单', group: '业务管理' },
      },
      {
        path: 'inventory/returns/:id',
        name: 'inventory-return-detail',
        component: () => loadRoute(() => import('@/views/inventory/DocumentDetail.vue')),
        meta: { title: '退料单详情', group: '业务管理' },
      },
      // 旧聚合书签：/inventory 重定向到库存余额（inventory-module-pages）
      {
        path: 'inventory',
        name: 'inventory-workbench',
        redirect: (to) => ({ name: 'inventory-balances', query: to.query }),
        meta: { title: '库存管理', group: '业务管理' },
      },
      // ===== IAM 域 =====
      {
        path: 'iam/employees',
        name: 'iam-employees',
        component: () => loadRoute(() => import('@/views/iam/Employees.vue')),
        meta: { title: '员工管理', group: '基础数据' },
      },
      {
        path: 'iam/customers',
        name: 'iam-customers',
        component: () => loadRoute(() => import('@/views/iam/Customers.vue')),
        meta: { title: '客户管理', group: '基础数据' },
      },
      {
        path: 'iam/customers/:id',
        name: 'iam-customer-detail',
        component: () => loadRoute(() => import('@/views/iam/CustomerDetail.vue')),
        meta: { title: '客户详情', group: '基础数据' },
      },
      // ===== Master 域 =====
      {
        path: 'master/materials',
        name: 'master-materials',
        component: () => loadRoute(() => import('@/views/master/Materials.vue')),
        meta: { title: '物料', group: '基础数据' },
      },
      {
        path: 'master/materials/:id',
        name: 'master-material-detail',
        component: () => loadRoute(() => import('@/views/master/MaterialDetail.vue')),
        meta: { title: '物料详情', group: '基础数据' },
      },
      {
        path: 'master/dictionaries',
        name: 'master-dictionaries',
        component: () => loadRoute(() => import('@/views/master/Dictionaries.vue')),
        meta: { title: '字典', group: '基础数据' },
      },
      {
        path: 'master/suppliers',
        name: 'master-suppliers',
        component: () => loadRoute(() => import('@/views/master/Suppliers.vue')),
        meta: { title: '厂商', group: '基础数据' },
      },
      {
        path: 'master/suppliers/:id',
        name: 'master-supplier-detail',
        component: () => loadRoute(() => import('@/views/master/SupplierDetail.vue')),
        meta: { title: `${ENTITY_LABEL.supplier}详情`, group: '基础数据' },
      },
      {
        path: 'master/external-pms',
        name: 'master-external-pms',
        component: () => loadRoute(() => import('@/views/master/ExternalProjectManagers.vue')),
        meta: { title: '项目经理', group: '客户管理' },
      },
      {
        path: 'master/workers',
        redirect: '/iam/employees',
      },
      {
        path: 'master/plan-templates',
        name: 'master-plan-templates',
        component: () => loadRoute(() => import('@/views/master/PlanTemplates.vue')),
        meta: { title: '施工模板管理', group: '基础数据' },
      },
      // ===== Finance 域 =====
      {
        path: 'finance/accounts',
        name: 'finance-accounts',
        component: () => loadRoute(() => import('@/views/finance/Accounts.vue')),
        meta: { title: '账户管理', group: '财务管理', permission: 'account.create' },
      },
      {
        path: 'finance/receipts',
        name: 'finance-receipts',
        component: () => loadRoute(() => import('@/views/finance/Receipts.vue')),
        meta: { title: '收款管理', group: '财务管理', permission: 'receipt.create' },
      },
      {
        path: 'finance/receipts/:id',
        name: 'finance-receipt-detail',
        component: () => loadRoute(() => import('@/views/finance/ReceiptDetail.vue')),
        meta: { title: '收款单详情', group: '财务管理', permission: 'receipt.create' },
      },
      {
        path: 'finance/costings',
        name: 'finance-costings',
        component: () => loadRoute(() => import('@/views/finance/Costings.vue')),
        meta: { title: '核算', group: '财务管理', permission: 'costing.create' },
      },
      {
        path: 'finance/costings/new',
        name: 'finance-costing-create',
        component: () => loadRoute(() => import('@/views/finance/CostingDetail.vue')),
        meta: { title: '新增核算', group: '财务管理', permission: 'costing.create' },
      },
      {
        path: 'finance/costings/:id',
        name: 'finance-costing-detail',
        component: () => loadRoute(() => import('@/views/finance/CostingDetail.vue')),
        meta: { title: '核算详情', group: '财务管理', permission: 'costing.create' },
      },
      {
        path: 'finance/payments',
        name: 'finance-payments',
        component: () => loadRoute(() => import('@/views/finance/Payments.vue')),
        meta: { title: '付款管理', group: '财务管理', permission: 'payment.create' },
      },
      {
        path: 'finance/payments/:id',
        name: 'finance-payment-detail',
        component: () => loadRoute(() => import('@/views/finance/PaymentDetail.vue')),
        meta: { title: '付款单详情', group: '财务管理', permission: 'payment.create' },
      },
      // 报表中心路由已临时隐藏（飞书任务 https://applink.feishu.cn/client/todo/detail?guid=4d69df0e-8f8c-4153-87d2-6752ba5225ea ）——本 change 按需恢复客户总账入口以满足对账单跳转
      {
        path: 'reports/customer-ledger',
        name: 'report-customer-ledger',
        component: () => loadRoute(() => import('@/views/report/CustomerLedgerReport.vue')),
        meta: { title: '客户总账', group: '报表中心' },
      },
      // {
      //   path: 'reports',
      //   name: 'report-center',
      //   component: () => loadRoute(() => import('@/views/report/ReportCenter.vue')),
      //   meta: { title: '报表中心', group: '报表中心' },
      // },
      // {
      //   path: 'reports/project-profit',
      //   name: 'report-project-profit',
      //   component: () => loadRoute(() => import('@/views/report/ProjectProfitReport.vue')),
      //   meta: { title: '项目利润表', group: '报表中心' },
      // },
      // {
      //   path: 'reports/inventory-current',
      //   name: 'report-inventory-current',
      //   component: () => loadRoute(() => import('@/views/report/InventoryCurrentReport.vue')),
      //   meta: { title: '当前库存', group: '报表中心' },
      // },
      // {
      //   path: 'reports/customer-unsettled',
      //   name: 'report-customer-unsettled',
      //   component: () => loadRoute(() => import('@/views/report/CustomerUnsettledReport.vue')),
      //   meta: { title: '客户未结表', group: '报表中心' },
      // },
      // {
      //   path: 'reports/supplier-ledger',
      //   name: 'report-supplier-ledger',
      //   component: () => loadRoute(() => import('@/views/report/SupplierLedgerReport.vue')),
      //   meta: { title: '供应商总账', group: '报表中心' },
      // },
    ],
  },
  {
    path: '/__route-load-error',
    name: 'route-load-error',
    component: () => import('@/views/RouteLoadError.vue'),
    meta: { public: true, title: '页面加载失败' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => loadRoute(() => import('@/views/auth/Forbidden.vue')),
    meta: { public: true, title: '页面不存在' },
  },
]

export const router = createRouter({
  // hash 路由：TCB 静态托管无错误文档/SpaFallback 配置入口，深链刷新会 404；
  // hash 模式请求始终落在根路径，由默认索引文档 index.html 兜底，刷新永不丢失路由。
  history: createWebHashHistory(),
  routes,
})

router.onError((error, to) => {
  // 懒加载失败时由静态错误页承接，避免 Router 启动 Promise 被拒绝后白屏。
  // 原始错误仍保留在控制台，供全局错误采集和 Vite/部署排查使用。
  // eslint-disable-next-line no-console
  console.error('[router] route component load failed', { to: to.fullPath, error })
  if (to.name !== 'route-load-error') {
    void router.replace({ name: 'route-load-error', query: { redirect: to.fullPath } })
  }
})

/**
 * 登录守卫（add-auth-login + add-platform-admin-web）：
 * - platform 路由（meta.platform=true）走平台守卫：员工 token 访问 /admin/* → /admin/login；
 *   无超管 token → /admin/login（带回跳）；有 token → 放行（刷新后经 platform-me 恢复身份）
 * - 会话独立：平台 token 不参与员工业务路由判断，两套 store 可同时持有各自会话
 * - 员工守卫：无 token → /login（带回跳目标）；meta.permission 不满足 → /403；
 *   登录态访问 /login → 回跳首页；mustChangePassword=true 阻断业务页 → /change-password
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const platformAuth = usePlatformAuthStore()

  // ===== 平台守卫（/admin/*）=====
  if (to.meta.platform) {
    // 平台登录页：public；已有超管会话则直接进入平台总览（避免与下方互斥重定向成环）
    if (to.name === 'platform-login') {
      if (platformAuth.hasAdmin) return { path: '/admin' }
      return true
    }
    // 员工 token 不作为超管会话：无平台 token 时访问 /admin/* → 平台登录
    if (auth.isLoggedIn && !platformAuth.hasAdmin) {
      return { path: '/admin/login' }
    }
    // 无超管 token → 跳平台登录（带回跳目标）
    if (!platformAuth.hasAdmin) {
      return { path: '/admin/login', query: { redirect: to.fullPath } }
    }
    // 刷新后只有 token 无身份 → platform-me 恢复；失败清态跳登录
    if (!platformAuth.admin) {
      try {
        const me = await apiPlatformMe()
        platformAuth.setIdentity(me)
      } catch {
        platformAuth.logout()
        return { path: '/admin/login', query: { redirect: to.fullPath } }
      }
    }
    if (platformAuth.mustChangePassword && to.name !== 'platform-change-password') {
      return { path: '/admin/change-password', query: { from: 'guard' } }
    }
    if (!platformAuth.mustChangePassword && to.name === 'platform-change-password') {
      return { path: '/admin' }
    }
    const platformPermission = to.meta.platformPermission
    if (typeof platformPermission === 'string' && !platformAuth.hasPermission(platformPermission)) {
      return { path: '/admin/forbidden' }
    }
    return true
  }

  // 报表中心已临时隐藏（飞书任务 https://applink.feishu.cn/client/todo/detail?guid=4d69df0e-8f8c-4153-87d2-6752ba5225ea ）：除客户总账（对账单跳转）外，其它 /reports 直链一律 403
  if (
    to.path.startsWith('/reports') &&
    to.path !== '/reports/customer-ledger' &&
    !to.path.startsWith('/reports/customer-ledger?')
  ) {
    return { path: '/403' }
  }

  // ===== 员工业务路由：仅由员工会话决定，不读取平台登录态 =====
  if (to.meta.public) {
    if (to.name === 'login' && auth.isLoggedIn) {
      return { path: '/' }
    }
    return true
  }

  if (!auth.isLoggedIn) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }

  // 刷新后身份信息可能只有 token（Pinia 已持久化 token，但 account 在内存）
  if (!auth.account && auth.isLoggedIn) {
    const token = auth.token
    const persistedToken = getSessionToken()
    const pageScope = getPageSessionScope()
    const ownsSession = (): boolean => auth.token === token &&
      getSessionToken() === persistedToken && getPageSessionScope() === pageScope
    const clearRemovedSession = (): void => {
      if (auth.token === token && getSessionToken() === null && getPageSessionScope() === pageScope) {
        auth.logout()
      }
    }
    // 内存会话不能覆盖已经更换的持久会话；被移除的旧会话只清理自己的 store。
    if (persistedToken !== token) {
      clearRemovedSession()
      return persistedToken === null ? { path: '/login', query: { redirect: to.fullPath } } : false
    }
    try {
      const me = await apiMe()
      if (!ownsSession()) {
        clearRemovedSession()
        return false
      }
      auth.setIdentity(me)
    } catch {
      if (!ownsSession()) {
        clearRemovedSession()
        return false
      }
      auth.logout()
      return { path: '/login', query: { redirect: to.fullPath } }
    }
  }

  const webRoleEligible = auth.roleCodes.includes('admin') ||
    auth.roleCodes.includes('finance') ||
    auth.role?.code === 'admin' ||
    auth.role?.code === 'finance'
  const webEligible = auth.accessChannel !== 'STAFF_MINI' && webRoleEligible
  if (!webEligible && to.name !== 'forbidden') {
    return { path: '/403' }
  }

  // 强制改密：阻断一切业务页
  if (auth.mustChangePassword && to.name !== 'change-password') {
    return { path: '/change-password', query: { from: 'guard' } }
  }

  const serviceStatus = auth.tenantService?.serviceStatus
  const serviceRestricted = serviceStatus === 'EXPIRED' || serviceStatus === 'UNOPENED'
  if (serviceRestricted && to.name !== 'subscription-expired' && to.name !== 'change-password') {
    return { path: '/subscription-expired' }
  }
  if (!serviceRestricted && to.name === 'subscription-expired') {
    return { path: '/' }
  }

  const permission = to.meta.permission as string | undefined
  if (permission && !auth.hasPermission(permission)) {
    return { path: '/403' }
  }

  return true
})
